import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import PersonnelSelect from "../components/common/PersonnelSelect";

/* ───────── Confirm Dialog ───────── */

function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel = "Confirm", danger = true }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded text-white ${danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────── Team Form Modal ───────── */

function TeamFormModal({ team, companies, onClose, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!team;

  // Derive the selected company's prefix
  const [companyId, setCompanyId] = useState(team?.company_id || user?.company_id || "");
  const selectedCompany = companies.find((c) => c.id === Number(companyId));
  const companyPrefix = selectedCompany?.prefix || "";

  // For edit mode, strip the prefix from the existing team_number if present
  const initialTeamNumber = (() => {
    if (!team?.team_number) return "";
    if (companyPrefix && team.team_number.startsWith(companyPrefix + "-")) {
      return team.team_number.slice(companyPrefix.length + 1);
    }
    return team.team_number;
  })();

  const [teamNumberSuffix, setTeamNumberSuffix] = useState(initialTeamNumber);
  const [personnel1Id, setPersonnel1Id] = useState(team?.default_personnel_1_id || null);
  const [personnel2Id, setPersonnel2Id] = useState(team?.default_personnel_2_id ?? null);
  const [dayOff, setDayOff] = useState(team?.day_off || "");
  const [dayOffStart, setDayOffStart] = useState(team?.day_off_start || "");
  const [isActive, setIsActive] = useState(team?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [personnelList, setPersonnelList] = useState([]);
  const [assignedIds, setAssignedIds] = useState([]);

  // Build the full team number from prefix + suffix
  const fullTeamNumber = companyPrefix
    ? `${companyPrefix}-${teamNumberSuffix.trim()}`
    : teamNumberSuffix.trim();

  // Fetch personnel when companyId changes
  useEffect(() => {
    if (!companyId) { setPersonnelList([]); return; }
    api.get("/team-masters/personnel", { params: { company_id: companyId } })
      .then(({ data }) => setPersonnelList(data))
      .catch(() => setPersonnelList([]));
  }, [companyId]);

  // Fetch assigned personnel IDs (excluding current team in edit mode)
  useEffect(() => {
    if (!companyId) { setAssignedIds([]); return; }
    const params = { company_id: companyId };
    if (isEdit && team?.id) params.exclude_team_id = team.id;
    api.get("/team-masters/assigned-personnel", { params })
      .then(({ data }) => setAssignedIds(data))
      .catch(() => setAssignedIds([]));
  }, [companyId, isEdit, team?.id]);

  // Build disabled sets for each dropdown
  const disabled1 = React.useMemo(() => {
    // Personnel 1: disabled if assigned elsewhere
    return new Set(assignedIds);
  }, [assignedIds]);

  const disabled2 = React.useMemo(() => {
    // Personnel 2: disabled if assigned elsewhere OR same as Personnel 1
    const s = new Set(assignedIds);
    if (personnel1Id) s.add(personnel1Id);
    return s;
  }, [assignedIds, personnel1Id]);

  const disabledReason = useCallback((uid) => {
    if (personnel1Id && uid === personnel1Id) return "Already selected as Personnel 1";
    if (assignedIds.includes(uid)) return "Already assigned to another team";
    return null;
  }, [assignedIds, personnel1Id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamNumberSuffix.trim()) { setError("Team number is required."); return; }
    if (!companyId) { setError("Company is required."); return; }
    if (!personnel1Id) { setError("Personnel 1 is required."); return; }
    if (personnel1Id && personnel2Id && personnel1Id === personnel2Id) {
      setError("Personnel 1 and Personnel 2 must not be the same."); return;
    }
    if (personnel1Id && disabled1.has(personnel1Id)) {
      setError("Personnel 1 is already assigned to another active team."); return;
    }
    if (personnel2Id && disabled2.has(personnel2Id)) {
      setError("Personnel 2 is already assigned to another active team."); return;
    }
    if (dayOff && !dayOffStart) {
      setError("Day-off start date is required when a day-off is selected."); return;
    }
    setSaving(true); setError("");
    try {
      const payload = {
        team_number: fullTeamNumber,
        company_id: Number(companyId),
        default_personnel_1_id: personnel1Id,
        default_personnel_2_id: personnel2Id || null,
        day_off: dayOff || null,
        day_off_start: dayOff && dayOffStart ? dayOffStart : null,
        is_active: isActive,
      };
      if (isEdit) {
        await api.put(`/team-masters/${team.id}`, {
          team_number: payload.team_number,
          default_personnel_1_id: payload.default_personnel_1_id,
          default_personnel_2_id: payload.default_personnel_2_id,
          day_off: payload.day_off,
          day_off_start: payload.day_off_start,
          is_active: payload.is_active,
        });
      } else {
        await api.post("/team-masters", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {isEdit ? "Edit Team" : "Add Team"}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4">
            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company <span className="text-red-500">*</span></label>
              {isEdit ? (
                <p className="text-sm text-gray-800 bg-gray-50 rounded px-3 py-2 border">{team.company_name}</p>
              ) : (
                <select
                  value={companyId}
                  onChange={(e) => { setCompanyId(Number(e.target.value)); setPersonnel1Id(null); setPersonnel2Id(null); setTeamNumberSuffix(""); }}
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Select Company —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Team Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Number <span className="text-red-500">*</span></label>
              <div className="flex">
                {companyPrefix && (
                  <span className="inline-flex items-center px-3 rounded-l border border-r-0 border-gray-300 bg-gray-100 text-gray-500 text-sm select-none">
                    {companyPrefix}-
                  </span>
                )}
                <input
                  type="text"
                  value={teamNumberSuffix}
                  onChange={(e) => setTeamNumberSuffix(e.target.value)}
                  className={`w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${companyPrefix ? "rounded-r" : "rounded"}`}
                  placeholder="e.g. 0024"
                  autoFocus
                />
              </div>
            </div>

            {/* Personnel 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Personnel 1 <span className="text-red-500">*</span></label>
              <PersonnelSelect
                value={personnel1Id}
                onChange={(id) => {
                  setPersonnel1Id(id);
                  // If Personnel 2 is the same as the new Personnel 1, clear it
                  if (id && personnel2Id === id) setPersonnel2Id(null);
                }}
                companyId={companyId}
                personnelList={personnelList}
                label="— Select Personnel 1 —"
                disabledIds={disabled1}
                disabledReason={disabledReason}
              />
            </div>

            {/* Personnel 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Personnel 2</label>
              <PersonnelSelect
                value={personnel2Id}
                onChange={(id) => setPersonnel2Id(id)}
                companyId={companyId}
                personnelList={personnelList}
                label="— Select Personnel 2 —"
                allowNone
                disabledIds={disabled2}
                disabledReason={disabledReason}
              />
              <p className="text-xs text-gray-500 mt-1">Optional. Select None if there is no second personnel.</p>
            </div>

            {/* Day-off */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day-off</label>
              <select
                value={dayOff}
                onChange={(e) => {
                  setDayOff(e.target.value);
                  if (!e.target.value) setDayOffStart("");
                }}
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— None —</option>
                <option value="Mon">Monday</option>
                <option value="Tue">Tuesday</option>
                <option value="Wed">Wednesday</option>
                <option value="Thu">Thursday</option>
                <option value="Fri">Friday</option>
                <option value="Sat">Saturday</option>
                <option value="Sun">Sunday</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Cancel/Resched won't be deducted on day-off unless team worked.</p>
            </div>

            {/* Day-off Start Date (only shown when day-off is selected) */}
            {dayOff && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day-off Start Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={dayOffStart}
                  onChange={(e) => setDayOffStart(e.target.value)}
                  min={(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split("T")[0]; })()}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Effective start date for this day-off. Can be backdated up to 30 days.</p>
              </div>
            )}

            {/* Status (edit only) */}
            {isEdit && (
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Active
                </label>
                <p className="text-xs text-gray-500 mt-1">Inactive teams won't appear in Team Number dropdowns.</p>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
            <button
              type="submit"
              disabled={saving || !teamNumberSuffix.trim() || !companyId || !personnel1Id}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ───────── Main Page ───────── */

export default function TeamsPage() {
  const { hasPerm, user } = useAuth();
  const canCreate = hasPerm("teams.create");
  const canEdit = hasPerm("teams.edit");
  const canDelete = hasPerm("teams.delete");
  const canManageAllCompanies = hasPerm("teams.manage_all_companies");
  const isMobile = useIsMobile();

  const [teams, setTeams] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany) params.company_id = filterCompany;
      const { data } = await api.get("/team-masters", { params });
      setTeams(data);
    } catch {
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [filterCompany]);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch {
      setCompanies([]);
    }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);
  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setError("");
    try {
      await api.delete(`/team-masters/${pendingDelete.id}`);
      setPendingDelete(null);
      fetchTeams();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed.");
      setPendingDelete(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/team-masters/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "teams_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download template.");
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/team-masters/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "teams_export.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to export teams.");
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const { data } = await api.post("/team-masters/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (data.created > 0) fetchTeams();
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || "Failed to import teams." });
    } finally {
      setImporting(false);
    }
  };

  const filtered = teams.filter((t) => {
    if (search) {
      const s = search.toLowerCase();
      return (
        t.team_number.toLowerCase().includes(s) ||
        t.company_name.toLowerCase().includes(s) ||
        t.default_personnel_1_name.toLowerCase().includes(s) ||
        (t.default_personnel_2_name || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <main className="flex-1 flex flex-col min-h-0 px-4 lg:px-6 pt-4 pb-2 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h1 className="text-xl font-semibold text-gray-800">Teams</h1>
          <div className="flex items-center gap-2">
            {canCreate && (
              <>
                <button
                  onClick={handleDownloadTemplate}
                  title="Download import template"
                  className="inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-800 border border-gray-300 shadow-sm transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Template
                </button>
                <button
                  onClick={() => { setImportFile(null); setImportResult(null); setShowImport(true); }}
                  title="Import teams from Excel"
                  className="inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded bg-white hover:bg-gray-50 text-green-700 hover:text-green-800 border border-green-300 shadow-sm transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  Import
                </button>
              </>
            )}
            <button
              onClick={handleExport}
              title="Export teams to Excel"
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-2 rounded bg-white hover:bg-gray-50 text-blue-700 hover:text-blue-800 border border-blue-300 shadow-sm transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Export
            </button>
            {canCreate && (
              <button
                onClick={() => { setEditTeam(null); setShowForm(true); }}
                className="bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700"
              >
                Add Team
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-3 flex-shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search teams..."
            className="border rounded px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {canManageAllCompanies && companies.length > 1 && (
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-3 flex-shrink-0">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">Loading...</div>
        ) : (
          <div className="flex-1 min-h-0 border rounded-lg overflow-hidden">
            <div className="h-full overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Team Number</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Company</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Default Personnel 1</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Default Personnel 2</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Day-off</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Updated</th>
                    {(canEdit || canDelete) && (
                      <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={(canEdit || canDelete) ? 8 : 7} className="text-center py-10 text-gray-400">
                        {search ? "No teams match your search." : "No teams yet. Click Add Team to get started."}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((t) => (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-800 font-medium">{t.team_number}</td>
                        <td className="px-4 py-3 text-gray-600">{t.company_name}</td>
                        <td className="px-4 py-3 text-gray-600">{t.default_personnel_1_name || <span className="text-gray-400">—</span>}</td>
                        <td className="px-4 py-3 text-gray-600">{t.default_personnel_2_name || <span className="text-gray-400 italic">None</span>}</td>
                        <td className="px-4 py-3 text-gray-600">{t.day_off || <span className="text-gray-400 italic">—</span>}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${t.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>
                            {t.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {new Date(t.updated_at).toLocaleDateString()}
                        </td>
                        {(canEdit || canDelete) && (
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {canEdit && (
                                <button
                                  onClick={() => { setEditTeam(t); setShowForm(true); }}
                                  className="text-xs px-3 py-1 border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
                                >
                                  Edit
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => setPendingDelete(t)}
                                  className="text-xs px-3 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {showForm && (
        <TeamFormModal
          team={editTeam}
          companies={companies}
          onClose={() => setShowForm(false)}
          onSaved={fetchTeams}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Delete Team"
          message={`Delete team "${pendingDelete.team_number}"? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import Teams</h3>
              <button onClick={() => setShowImport(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>

            {!importResult ? (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Upload an Excel file (.xlsx) with columns: <span className="font-medium">Team Number, Company, Default Personnel 1, Default Personnel 2</span>.
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Personnel names must match the <span className="font-medium">display name</span> of an active FST/FST MIG account.
                  Teams will still be created if personnel names don't match — they'll just have empty personnel fields.
                </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                  {importFile && (
                    <p className="text-xs text-gray-500 mt-2">Selected: <span className="font-medium text-gray-700">{importFile.name}</span></p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowImport(false)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={!importFile || importing}
                    className="px-4 py-2 text-sm text-white rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {importing ? "Importing…" : "Import"}
                  </button>
                </div>
              </>
            ) : importResult.error ? (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-700 font-medium">{importResult.error}</p>
                </div>
                <div className="flex justify-end">
                  <button onClick={() => setShowImport(false)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 text-gray-600">Close</button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-green-800 mb-2">Import Complete</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xl font-bold text-gray-900">{importResult.total}</p>
                      <p className="text-xs text-gray-500">Total Rows</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-green-700">{importResult.created}</p>
                      <p className="text-xs text-gray-500">Created</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-amber-600">{importResult.skipped}</p>
                      <p className="text-xs text-gray-500">Skipped</p>
                    </div>
                  </div>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                    <p className="text-xs font-semibold text-amber-700 mb-1">Details:</p>
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-amber-600">{err}</p>
                    ))}
                  </div>
                )}
                <div className="flex justify-end">
                  <button onClick={() => setShowImport(false)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
